import React, { useEffect, useRef, useState } from "react";
import Newsletter from "../../components/Newsletter";
import {
    FileText,
    RefreshCw,
    Undo2,
    AlertTriangle,
    Ban,
    Receipt,
} from "lucide-react";
import "../../styles/terms.css";

// ──────────────────────────────────────────────────
// Terms & Conditions Page
// Route: /terms-and-conditions
// ──────────────────────────────────────────────────

const SECTIONS = [
    { id: "acceptance", label: "Acceptance of Terms" },
    { id: "amendments", label: "Amendments" },
    { id: "refund", label: "Refund & Cancellation" },
];

const REFUND_POINTS = [
    {
        icon: AlertTriangle,
        text:
            "Any payment made multiple times due to error, or any other kind of erroneous payment may be brought to the notice of the Site officials and the payment would be refunded if the Site official is satisfied with the cause and/or reason of error by the payee.",
    },
    {
        icon: FileText,
        text:
            "All donations and/or payment made through the Site, the acceptance of the same is at the sole discretion of the temple management and its trustees. The management reserves its sole rights to accept or cancel any donation and/or payment without assigning any reason thereof.",
    },
    {
        icon: Ban,
        text:
            "Please note that donations once made cannot be charged back, except in cases of fraudulent use, once investigation is carried out by necessary governmental / Bank authorities and proofs substantiated.",
    },
    {
        icon: Receipt,
        text:
            "Incase any donation receipt is issued against the same, that shall stand cancelled, and any income tax benefit so derived shall be null and void",
    },
];

export default function TermsAndConditions() {
    const [activeId, setActiveId] = useState("acceptance");
    const sectionRefs = useRef({});

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveId(entry.target.id);
                    }
                });
            },
            { rootMargin: "-15% 0px -70% 0px", threshold: 0 }
        );

        SECTIONS.forEach(({ id }) => {
            const el = sectionRefs.current[id];
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, []);

    const handleNavClick = (id) => (e) => {
        e.preventDefault();
        sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className="iskcon-terms-page">
            {/* ── Hero ── */}
            {/* <section className="iskcon-terms-hero">
                <div className="iskcon-terms-hero-overlay" />
                <div className="container iskcon-terms-hero-inner">
                <span className="iskcon-terms-hero-eyebrow">ISKCON DELHI</span>
                <h1 className="iskcon-terms-hero-title">Terms &amp; Conditions</h1>
                <p className="iskcon-terms-hero-subtitle">
                Please read these terms carefully before using this Site
                </p>
                </div>
                </section> */}

            <section className="iskcon-terms-section">
                <h3 className="iskcon-terms-header">Terms and Conditions</h3>
                <div className="container">
                    {/* ── Meta strip ── */}
                    <div className="iskcon-terms-meta">
                        <span className="iskcon-terms-meta-badge">Last revised: February 21, 2021</span>
                        <p className="iskcon-terms-meta-text">
                            ISKCON Delhi Offerings is an integral part of the International Society of Krishna Consciousness (ISKCON) Sant Nagar, East of Kailash, New Delhi for the purpose of accepting donations. So the website {" "}
                            <a
                                href="https://www.iskcondelhiofferings.com"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                www.iskcondelhiofferings.com
                            </a>{" "}
                            (the &ldquo;SITE&rdquo;) is fully owned by the International
                            Society of Krishna Consciousness (ISKCON), Sant Nagar, East of
                            Kailash, New Delhi.
                        </p>
                    </div>

                    <div className="iskcon-terms-layout">
                        {/* ── Sticky TOC ── */}
                        <aside className="iskcon-terms-toc">
                            <span className="iskcon-terms-toc-label">On this page</span>
                            <nav>
                                <ul>
                                    {SECTIONS.map((s) => (
                                        <li key={s.id}>
                                            <a
                                                href={`#${s.id}`}
                                                className={activeId === s.id ? "is-active" : ""}
                                                onClick={handleNavClick(s.id)}
                                            >
                                                {s.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        </aside>

                        {/* ── Main content ── */}
                        <div className="iskcon-terms-content">
                            {/* Acceptance of Terms */}
                            <div
                                id="acceptance"
                                ref={(el) => (sectionRefs.current.acceptance = el)}
                                className="iskcon-terms-block"
                            >
                                <h4 className="iskcon-terms-heading">
                                    <FileText size={20} className="iskcon-terms-heading-icon" />
                                    Acceptance of Terms
                                </h4>
                                <p className="iskcon-terms-body">
                                    Your use of the Site is governed by the policies, terms, and conditions set forth below. Please read the following information carefully. By using this Site or donating through the site, you indicate your acceptance of, and agreement to be bound by, the terms set forth below. If you do not agree to these terms and conditions, please do not use this Site.
                                </p>

                            </div>

                            {/* Amendments */}
                            <div
                                id="amendments"
                                ref={(el) => (sectionRefs.current.amendments = el)}
                                className="iskcon-terms-block"
                            >
                                <h4 className="iskcon-terms-heading">
                                    <RefreshCw size={20} className="iskcon-terms-heading-icon" />
                                    Amendments
                                </h4>
                                <p className="iskcon-terms-body">
                                    These terms and conditions may be changed by ISKCON in the future. It is your responsibility as a user to periodically return to this page to review the terms and conditions for amendments. The amended terms shall take effect automatically the day they are posted on the site. Your continued use of the web site following any amendments will constitute agreement to such amendments. These Terms of Service were last revised on February 21st, 2021.
                                </p>
                                <p className="iskcon-terms-body">
                                    Your continued usage of the site, shall be in acceptance to the terms that may be updated in future with retrospective effect. In case you do not agree to any terms your sole remedy is to stop using the website.
                                </p>
                            </div>

                            {/* Refund and Cancellation Policy */}
                            <div
                                id="refund"
                                ref={(el) => (sectionRefs.current.refund = el)}
                                className="iskcon-terms-block"
                            >
                                <h4 className="iskcon-terms-heading">
                                    <Undo2 size={20} className="iskcon-terms-heading-icon" />
                                    Refund and Cancellation Policy
                                </h4>
                                <p className="iskcon-terms-body">
                                    ISKCON currently accepts donations through its Online
                                    Payment Gateway Facility for various charitable / social
                                    purposes.
                                </p>
                                <ul className="iskcon-terms-ledger">
                                    {REFUND_POINTS.map((point, i) => {
                                        const Icon = point.icon;
                                        return (
                                            <li key={i} className="iskcon-terms-ledger-item">
                                                <span className="iskcon-terms-ledger-node">
                                                    <Icon size={16} />
                                                </span>
                                                <p>{point.text}</p>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Newsletter />
        </div>
    );
}